// src/pages/sessions/components/LiveSession/mobile/PhoneBottomNav.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  VideoCameraIcon,
  ChatBubbleLeftRightIcon,
  UsersIcon,
  Squares2X2Icon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';

/**
 * PhoneBottomNav
 *
 * Bottom navigation for the phone layout. Safe-area-aware so the home
 * indicator on iPhone doesn't overlap the buttons.
 *
 * Tabs:
 *  - video      always
 *  - chat       always, with unread badge
 *  - people     always
 *  - whiteboard only when isWhiteboardActive
 *  - rooms      only for teachers
 */
const PhoneBottomNav = ({
  activeView,
  onViewChange,
  isTeacher,
  hasUnreadMessages,
  unreadCount = 0,
  isWhiteboardActive = false,
}) => {
  const { t } = useTranslation();

  const tabs = [
    {
      id: 'video',
      icon: VideoCameraIcon,
      label: t('liveSession.media.video', 'Video'),
      always: true,
    },
    {
      id: 'chat',
      icon: ChatBubbleLeftRightIcon,
      label: t('liveSession.sidebar.chat', 'Chat'),
      always: true,
      badge: hasUnreadMessages && activeView !== 'chat' ? unreadCount : 0,
    },
    {
      id: 'people',
      icon: UsersIcon,
      label: t('liveSession.sidebar.participants', 'Deelnemers'),
      always: true,
    },
    {
      id: 'whiteboard',
      icon: PencilSquareIcon,
      label: t('controls.openWhiteboard', 'Whiteboard'),
      always: false,
      show: isWhiteboardActive,
    },
    {
      id: 'rooms',
      icon: Squares2X2Icon,
      label: t('liveSession.sidebar.rooms', 'Groepen'),
      always: false,
      show: isTeacher,
    },
  ];

  const visibleTabs = tabs.filter(tab => tab.always || tab.show);

  return (
    <div
      className="bg-gray-800 border-t border-gray-700 flex-shrink-0"
      style={{ paddingBottom: 'max(8px, env(safe-area-inset-bottom))' }}
    >
      <div className="flex justify-around">
        {visibleTabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeView === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => onViewChange(tab.id)}
              className={`flex flex-col items-center justify-center flex-1 relative transition-colors
                ${isActive ? 'text-cyan-500' : 'text-gray-400'}
              `}
              style={{ minHeight: 56, minWidth: 44 }}
            >
              <Icon className="w-6 h-6" />
              <span className="text-[10px] mt-0.5 font-medium">{tab.label}</span>
              {tab.badge > 0 && (
                <span className="absolute top-1 right-2 min-w-[18px] h-[18px] bg-red-500 rounded-full text-white text-[10px] flex items-center justify-center font-semibold">
                  {tab.badge > 99 ? '99+' : tab.badge}
                </span>
              )}
              {/* Active dot indicator */}
              {isActive && (
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-cyan-500 rounded-full" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PhoneBottomNav;
